<div
  class="ce-element"
  [class.ce-element--selected]="isSelected"
  [class.ce-element--empty]="isEmpty"
  [style]="elementStyle"
  [attr.data-element-id]="element.id"
  [attr.data-original-y]="originalYMm"
  [attr.data-configured-height]="element.height"
  [attr.data-is-auto]="isAutoHeightElement ? 'true' : null"
  (mousedown)="startDrag($event)"
  (click)="onClick($event)">

  <!-- ── Field ─────────────────────────────────────────────────────────────── -->
  <ng-container *ngIf="element.elementType === 'field'">
    <span class="ce-label" *ngIf="element.showLabel" [style]="labelStyle">{{ element.label }}:</span>
    <span class="ce-value" [style]="textStyle" [innerHTML]="displayValue"></span>
  </ng-container>

  <!-- ── Header ────────────────────────────────────────────────────────────── -->
  <ng-container *ngIf="element.elementType === 'header'">
    <span class="ce-header-text" [style]="textStyle">{{ displayValue }}</span>
  </ng-container>

  <!-- ── Divider ───────────────────────────────────────────────────────────── -->
  <!-- Use a <div> rather than <hr> — <hr> carries UA styles (border-style:inset,
       overflow:hidden) that interfere with print rendering across browsers. -->
  <ng-container *ngIf="element.elementType === 'divider'">
    <div class="ce-divider" [style.borderTopColor]="element.color"></div>
  </ng-container>

  <!-- ── Barcode (uses existing NgxBarcode6Module) ─────────────────────────── -->
  <ng-container *ngIf="element.elementType === 'barcode'">
    <span class="ce-label" *ngIf="element.showLabel" [style]="labelStyle">{{ element.label }}</span>
    <div class="ce-barcode-wrap">
      <ngx-barcode6
        [bc-value]="barcodeValue"
        [bc-display-value]="false"
        [bc-height]="barcodeHeight"
        [bc-width]="2"
        [bc-format]="'CODE128'"
        [bc-element-type]="'svg'"
        bc-background="transparent">
      </ngx-barcode6>
    </div>
  </ng-container>

  <!-- ── QR Code (uses existing QRCodeModule) ──────────────────────────────── -->
  <ng-container *ngIf="element.elementType === 'qrcode'">
    <span class="ce-label" *ngIf="element.showLabel" [style]="labelStyle">{{ element.label }}</span>
    <div class="ce-qr-wrap">
      <qrcode
        [qrdata]="qrValue"
        [width]="qrSize"
        [margin]="1"
        colorDark="#000000"
        colorLight="#ffffff"
        usesvg="true">
      </qrcode>
    </div>
  </ng-container>

  <!-- ── Image ─────────────────────────────────────────────────────────────── -->
  <ng-container *ngIf="element.elementType === 'image'">
    <span class="ce-label" *ngIf="element.showLabel" [style]="labelStyle">{{ element.label }}</span>

    <!-- Live attachments: render each URL as an <img> -->
    <div class="ce-attachment-grid" *ngIf="imageUrls.length > 0">
      <img *ngFor="let url of imageUrls"
           [src]="url"
           class="ce-attachment-img"
           [attr.alt]="'bikeshop.receipt_template.element.attachment_alt' | translate"
           (load)="contentResized.emit()"
           (error)="contentResized.emit()" />
    </div>

    <!-- Design mode / no data: placeholder icon -->
    <div class="ce-image-placeholder" *ngIf="imageUrls.length === 0">
      <i class="pi pi-image"></i>
      <span>{{ element.label }}</span>
    </div>
  </ng-container>

  <!-- ── Barcode Table ──────────────────────────────────────────────────────── -->
  <!-- Renders one row per line item; first column = SKU as a barcode, rest = text -->
  <ng-container *ngIf="element.elementType === 'barcode-table'">
    <span class="ce-label" *ngIf="element.showLabel" [style]="labelStyle">{{ element.label }}</span>

    <ng-container *ngIf="tableRows !== null; else barcodeTablePlaceholder">
      <table class="ce-table ce-table--barcode" [style.fontSize]="textStyle['fontSize']" [style.color]="element.color">
        <colgroup>
          <col *ngFor="let w of columnWidthsForTable" [style.width]="w + '%'">
        </colgroup>
        <thead *ngIf="isSelected" class="ce-col-resize-head">
          <tr>
            <th *ngFor="let w of columnWidthsForTable; let i = index; let last = last">
              <span class="ce-col-align-btn" [title]="columnAlignmentsForTable[i]"
                    (click)="cycleColumnAlignment(i); $event.stopPropagation()">
                <i [class]="alignIcon(columnAlignmentsForTable[i])"></i>
              </span>
              <div *ngIf="!last" class="ce-col-resize-handle"
                   (mousedown)="startColumnResize($event, i)"
                   (click)="$event.stopPropagation()"></div>
            </th>
          </tr>
        </thead>
        <tr *ngFor="let row of tableRows">
          <!-- Column 0: product SKU rendered as a barcode -->
          <td class="ce-barcode-col" [style.textAlign]="columnAlignmentsForTable[0]">
            <ng-container *ngIf="row[0]; else emptyBarcode">
              <ngx-barcode6
                [bc-value]="row[0]"
                [bc-display-value]="false"
                [bc-height]="barcodeTableRowHeight"
                [bc-width]="1.2"
                [bc-format]="'CODE128'"
                [bc-element-type]="'svg'"
                bc-background="transparent">
              </ngx-barcode6>
            </ng-container>
            <ng-template #emptyBarcode><span class="ce-barcode-empty">—</span></ng-template>
          </td>
          <!-- Remaining columns: plain text -->
          <td *ngFor="let cell of row | slice:1; let i = index"
              [style.textAlign]="columnAlignmentsForTable[i + 1]">{{ cell }}</td>
        </tr>
      </table>
    </ng-container>

    <ng-template #barcodeTablePlaceholder>
      <table class="ce-table ce-table--barcode ce-table--placeholder" [style.fontSize]="textStyle['fontSize']">
        <colgroup>
          <col *ngFor="let w of columnWidthsForTable" [style.width]="w + '%'">
        </colgroup>
        <thead *ngIf="isSelected" class="ce-col-resize-head">
          <tr>
            <th *ngFor="let w of columnWidthsForTable; let i = index; let last = last">
              <span class="ce-col-align-btn" [title]="columnAlignmentsForTable[i]"
                    (click)="cycleColumnAlignment(i); $event.stopPropagation()">
                <i [class]="alignIcon(columnAlignmentsForTable[i])"></i>
              </span>
              <div *ngIf="!last" class="ce-col-resize-handle"
                   (mousedown)="startColumnResize($event, i)"
                   (click)="$event.stopPropagation()"></div>
            </th>
          </tr>
        </thead>
        <tr>
          <td class="ce-barcode-col"><small class="ce-barcode-placeholder-label">&#9641; SKU barcode</small></td>
          <td>Description</td><td>Qty</td><td>Total</td>
        </tr>
        <tr>
          <td class="ce-barcode-col"><small class="ce-barcode-placeholder-label">&#9641; SKU barcode</small></td>
          <td>Description</td><td>Qty</td><td>Total</td>
        </tr>
      </table>
    </ng-template>
  </ng-container>

  <!-- ── Table ─────────────────────────────────────────────────────────────── -->
  <ng-container *ngIf="element.elementType === 'table'">
    <span class="ce-label" *ngIf="element.showLabel" [style]="labelStyle">{{ element.label }}</span>

    <!-- Live data: render real rows (preview mode or job card loaded) -->
    <ng-container *ngIf="tableRows !== null; else tablePlaceholder">
      <table [class.ce-table]="true" [class.ce-table--notes]="isNotesTable()" [style.fontSize]="textStyle['fontSize']" [style.color]="element.color">
        <colgroup>
          <col *ngFor="let w of columnWidthsForTable" [style.width]="w + '%'">
        </colgroup>
        <thead *ngIf="isSelected" class="ce-col-resize-head">
          <tr>
            <th *ngFor="let w of columnWidthsForTable; let i = index; let last = last">
              <span class="ce-col-align-btn" [title]="columnAlignmentsForTable[i]"
                    (click)="cycleColumnAlignment(i); $event.stopPropagation()">
                <i [class]="alignIcon(columnAlignmentsForTable[i])"></i>
              </span>
              <div *ngIf="!last" class="ce-col-resize-handle"
                   (mousedown)="startColumnResize($event, i)"
                   (click)="$event.stopPropagation()"></div>
            </th>
          </tr>
        </thead>
        <tr *ngFor="let row of tableRows">
          <!-- Group header row: spans all columns, displayed as a section heading -->
          <ng-container *ngIf="isGroupHeaderRow(row)">
            <td [attr.colspan]="tableColumnCount" class="ce-table-group-header">{{ row[1] }}</td>
          </ng-container>
          <!-- Regular data row -->
          <ng-container *ngIf="!isGroupHeaderRow(row)">
            <td *ngFor="let cell of row; let i = index"
                [style.textAlign]="columnAlignmentsForTable[i]">
              <!-- Checkbox cell -->
              <input *ngIf="isCheckboxCell(cell)"
                     type="checkbox"
                     [attr.checked]="checkboxChecked(cell) ? '' : null"
                     (click)="$event.preventDefault()"
                     style="cursor:default;pointer-events:none;accent-color:#000000;">
              <!-- Traffic light cell -->
              <span *ngIf="isTrafficLightCell(cell)" class="ce-tl">
                <span class="ce-tl-dot ce-tl-dot--red"    [class.ce-tl-dot--active]="trafficLightValue(cell) === 'Yes'"></span>
                <span class="ce-tl-dot ce-tl-dot--orange" [class.ce-tl-dot--active]="trafficLightValue(cell) === 'No'"></span>
                <span class="ce-tl-dot ce-tl-dot--green"  [class.ce-tl-dot--active]="trafficLightValue(cell) === 'Not applicable'"></span>
                <!-- Non-front/rear: plain text, no label -->
                <span *ngIf="trafficLightText(cell)" class="ce-tl-fr">{{trafficLightText(cell)}}</span>
                <!-- Front/rear: labelled F: and R: -->
                <span *ngIf="trafficLightFront(cell)" class="ce-tl-fr"><b>F:</b> {{trafficLightFront(cell)}}</span>
                <span *ngIf="trafficLightRear(cell)" class="ce-tl-fr"><b>R:</b> {{trafficLightRear(cell)}}</span>
              </span>
              <!-- Text cell -->
              <span *ngIf="!isCheckboxCell(cell) && !isTrafficLightCell(cell)">{{ cell }}</span>
            </td>
          </ng-container>
        </tr>
      </table>
    </ng-container>

    <!-- Design mode: generic placeholder so the element is visible on canvas -->
    <ng-template #tablePlaceholder>
      <table class="ce-table ce-table--placeholder" [style.fontSize]="textStyle['fontSize']">
        <colgroup>
          <col *ngFor="let w of columnWidthsForTable" [style.width]="w + '%'">
        </colgroup>
        <thead *ngIf="isSelected" class="ce-col-resize-head">
          <tr>
            <th *ngFor="let w of columnWidthsForTable; let i = index; let last = last">
              <span class="ce-col-align-btn" [title]="columnAlignmentsForTable[i]"
                    (click)="cycleColumnAlignment(i); $event.stopPropagation()">
                <i [class]="alignIcon(columnAlignmentsForTable[i])"></i>
              </span>
              <div *ngIf="!last" class="ce-col-resize-handle"
                   (mousedown)="startColumnResize($event, i)"
                   (click)="$event.stopPropagation()"></div>
            </th>
          </tr>
        </thead>
        <tr><td>{{ element.label }} row 1</td><td>—</td></tr>
        <tr><td>{{ element.label }} row 2</td><td>—</td></tr>
      </table>
    </ng-template>
  </ng-container>

  <!-- ── Free Text ────────────────────────────────────────────────────────── -->
  <ng-container *ngIf="element.elementType === 'text'">
    <!-- Editing mode: textarea fills the element -->
    <textarea
      *ngIf="isEditing"
      #textAreaEl
      class="ce-text-editor"
      [style]="textStyle"
      [value]="element.content"
      (blur)="stopEdit()"
      (input)="onTextareaInput()"
      (keydown)="onEditKeyDown($event)">
    </textarea>
    <!-- Display mode: shows content, dblclick enters edit -->
    <span
      *ngIf="!isEditing"
      class="ce-text-display"
      [style]="textStyle"
      (dblclick)="startEdit($event)">
      {{ element.content || ('bikeshop.receipt_template.element.free_text_placeholder' | translate) }}
    </span>
  </ng-container>

  <!-- ── Selection handles (hidden during multi-select to keep the canvas clean) ─ -->
  <ng-container *ngIf="isSelected && showHandles">
    <div class="ce-handle ce-handle--nw" (mousedown)="startResize($event, 'nw')"></div>
    <div class="ce-handle ce-handle--n"  (mousedown)="startResize($event, 'n')"></div>
    <div class="ce-handle ce-handle--ne" (mousedown)="startResize($event, 'ne')"></div>
    <div class="ce-handle ce-handle--e"  (mousedown)="startResize($event, 'e')"></div>
    <div class="ce-handle ce-handle--se" (mousedown)="startResize($event, 'se')"></div>
    <div class="ce-handle ce-handle--s"  (mousedown)="startResize($event, 's')"></div>
    <div class="ce-handle ce-handle--sw" (mousedown)="startResize($event, 'sw')"></div>
    <div class="ce-handle ce-handle--w"  (mousedown)="startResize($event, 'w')"></div>
  </ng-container>

</div>
